import React, { useMemo } from 'react';
import { useBooks } from '../context/BookContext';
import { computeMilestones } from '../utils/milestones';
import { WOOD_THEMES, DEFAULT_WOOD_THEME_KEY } from '../themes/woodThemes';

// How many not-yet-earned milestones to tease after the earned ones.
const LOCKED_PREVIEW = 3;

const formatDetail = (detail) => {
  if (!detail || !/^\d{4}-\d{2}-\d{2}/.test(detail)) return detail;
  const d = new Date(detail.slice(0, 10) + 'T00:00:00');
  return d.toLocaleDateString(undefined, { month: 'short', year: 'numeric' });
};

const Milestones = () => {
  const { books, logs } = useBooks();
  const milestones = useMemo(() => computeMilestones(books, logs), [books, logs]);
  const earned = milestones.filter(m => m.earned);
  const locked = milestones.filter(m => !m.earned).slice(0, LOCKED_PREVIEW);

  return (
    <div style={cardStyle}>
      <div style={headerStyle}>
        <span style={titleStyle}>My Milestones</span>
        <span style={countStyle}>{earned.length} of {milestones.length}</span>
      </div>
      <div style={gridStyle}>
        {earned.map(m => (
          <div key={m.key} style={tileStyle} title={m.book?.title}>
            {m.book?.cover ? (
              <img src={m.book.cover} alt={m.book.title} style={coverStyle} />
            ) : (
              <div style={{ ...coverStyle, ...fallbackCoverStyle }}>{m.book?.title}</div>
            )}
            <div style={labelStyle}>{m.label}</div>
            {m.detail && <div style={detailStyle}>{formatDetail(m.detail)}</div>}
          </div>
        ))}
        {locked.map(m => (
          <div key={m.key} style={{ ...tileStyle, opacity: 0.55 }}>
            <div style={{ ...coverStyle, ...lockedCoverStyle }}>{m.hint}</div>
            <div style={labelStyle}>{m.label}</div>
          </div>
        ))}
      </div>
    </div>
  );
};

const wood = WOOD_THEMES[DEFAULT_WOOD_THEME_KEY];

const cardStyle = {
  backgroundColor: 'var(--bg-card)', padding: '1.25rem', borderRadius: '20px',
  border: '1px solid var(--border)', boxShadow: '0 4px 20px rgba(0,0,0,0.03)', marginBottom: '1.5rem'
};
const headerStyle = { display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '1rem' };
const titleStyle = { fontSize: '0.75rem', fontWeight: 'bold', color: 'var(--text-soft)', letterSpacing: '0.05rem', textTransform: 'uppercase' };
const countStyle = { fontSize: '0.75rem', color: 'var(--text-soft)' };
const gridStyle = { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(90px, 1fr))', gap: '16px 12px' };
const tileStyle = { display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' };
const coverStyle = {
  width: '80px', height: '120px', objectFit: 'cover', borderRadius: '4px',
  boxShadow: '0 3px 8px rgba(0,0,0,0.25)', marginBottom: '8px'
};
const fallbackCoverStyle = {
  display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '6px', boxSizing: 'border-box',
  backgroundColor: wood.drawer, color: wood.knob, fontSize: '0.7rem', fontWeight: 'bold', overflow: 'hidden'
};
const lockedCoverStyle = {
  display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '8px', boxSizing: 'border-box',
  border: '2px dashed var(--border)', boxShadow: 'none', color: 'var(--text-soft)', fontSize: '0.68rem', lineHeight: 1.3
};
const labelStyle = { fontSize: '0.75rem', fontWeight: 'bold', color: 'var(--text-main)' };
const detailStyle = { fontSize: '0.68rem', color: 'var(--text-soft)', marginTop: '2px' };

export default Milestones;
